import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  FaArrowLeft,
  FaArrowRight,
  FaCalendarAlt,
  FaMapMarkerAlt,
  FaTrophy,
  FaUsers,
} from "react-icons/fa";
import { getAllEvents } from "../../services/eventServices";

const FALLBACK_IMAGE = "/img/no-image.jpg";

function isTodayOrFuture(dateValue) {
  if (!dateValue) return false;
  const d = new Date(dateValue);
  if (Number.isNaN(d.getTime())) return false;

  const eventDay = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const today = new Date();
  const todayStart = new Date(
    today.getFullYear(),
    today.getMonth(),
    today.getDate(),
  );

  return eventDay >= todayStart;
}

function formatDateRange(start, end) {
  if (!start) return "Date à confirmer";

  const startDate = new Date(start);
  const endDate = end ? new Date(end) : null;

  const sameDay =
    endDate &&
    startDate.getFullYear() === endDate.getFullYear() &&
    startDate.getMonth() === endDate.getMonth() &&
    startDate.getDate() === endDate.getDate();

  const startLabel = startDate.toLocaleDateString("fr-FR", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });

  if (!endDate || sameDay) return startLabel;

  const endLabel = endDate.toLocaleDateString("fr-FR", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });

  return `${startLabel} → ${endLabel}`;
}

function formatShortDay(dateValue) {
  if (!dateValue) return { day: "--", month: "---" };

  const d = new Date(dateValue);

  return {
    day: d.toLocaleDateString("fr-FR", { day: "2-digit" }),
    month: d.toLocaleDateString("fr-FR", { month: "short" }).toUpperCase(),
  };
}

function EventCard({ event }) {
  const navigate = useNavigate();
  const imageSrc = event?.photo_url?.trim() ? event.photo_url : FALLBACK_IMAGE;
  const shortDate = formatShortDay(event.start);

  return (
    <article className="group relative w-[86%] shrink-0 snap-start overflow-hidden rounded-[24px] border border-slate-200 bg-white shadow-sm transition-all duration-300 hover:-translate-y-1 hover:shadow-lg sm:w-[48%] lg:w-[31.6%]">
      <div className="relative h-48 overflow-hidden bg-slate-100">
        <img
          src={imageSrc}
          alt={event?.titre || "Événement"}
          className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
          onError={(e) => {
            e.currentTarget.src = FALLBACK_IMAGE;
          }}
        />

        <div className="absolute inset-0 bg-gradient-to-t from-slate-950/65 via-slate-950/20 to-transparent" />

        <div className="absolute left-4 top-4 rounded-2xl bg-white/95 px-3 py-2 text-center shadow-md backdrop-blur">
          <div className="text-lg font-black leading-none text-slate-900">
            {shortDate.day}
          </div>
          <div className="mt-1 text-[10px] font-bold tracking-[0.18em] text-slate-500">
            {shortDate.month}
          </div>
        </div>

        {event?.level_event && (
          <div className="absolute bottom-4 left-4 rounded-full bg-emerald-500/95 px-3 py-1 text-xs font-bold text-white shadow">
            {event.level_event}
          </div>
        )}
      </div>

      <div className="p-5">
        <div className="mb-3 flex flex-wrap gap-2">
          {event?.type_event && (
            <span className="rounded-full bg-blue-50 px-2.5 py-1 text-[11px] font-semibold text-blue-700">
              {event.type_event}
            </span>
          )}
          {event?.organisateur && (
            <span className="rounded-full bg-violet-50 px-2.5 py-1 text-[11px] font-semibold text-violet-700">
              {event.organisateur}
            </span>
          )}
        </div>

        <h3 className="line-clamp-3 font-bold leading-tight text-slate-900">
          {event?.titre || "Événement"}
        </h3>

        <div className="mt-3 space-y-2 text-sm text-slate-600">
          <div className="flex items-center gap-2">
            <FaCalendarAlt className="shrink-0 text-slate-400" />
            <span>{formatDateRange(event?.start, event?.end)}</span>
          </div>

          <div className="flex items-center gap-2">
            <FaMapMarkerAlt className="shrink-0 text-slate-400" />
            <span>{event?.lieu_event || "Lieu à confirmer"}</span>
          </div>

          {event?.cate_event && (
            <div className="flex items-center gap-2">
              <FaUsers className="shrink-0 text-slate-400" />
              <span>{event.cate_event}</span>
            </div>
          )}
        </div>

        <div className="mt-5 flex items-center justify-between">
          <span className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">
            À venir
          </span>

          <button
            type="button"
            onClick={() => navigate("/calendrier-ligue")}
            className="inline-flex items-center gap-2 rounded-full bg-slate-900 px-3 py-2 text-xs font-bold text-white transition hover:bg-slate-800"
          >
            Voir
            <FaArrowRight className="text-[10px]" />
          </button>
        </div>
      </div>
    </article>
  );
}

function HomeUpcomingEvents() {
  const navigate = useNavigate();
  const scrollRef = useRef(null);

  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let isMounted = true;

    async function loadEvents() {
      try {
        setLoading(true);
        const data = await getAllEvents();

        if (!isMounted) return;

        const filtered = (Array.isArray(data) ? data : [])
          .filter(
            (event) =>
              event &&
              isTodayOrFuture(event.start) &&
              String(event.organisateur || "").trim() !== "" &&
              ["Compétition", "Animation"].includes(
                String(event.type_event || "").trim(),
              ),
          )
          .sort((a, b) => new Date(a.start) - new Date(b.start))
          .slice(0, 10);

        setEvents(filtered);
      } catch (error) {
        console.error("Erreur chargement événements à venir :", error);
        if (isMounted) setEvents([]);
      } finally {
        if (isMounted) setLoading(false);
      }
    }

    loadEvents();

    return () => {
      isMounted = false;
    };
  }, []);

  const hasEvents = useMemo(() => events.length > 0, [events]);

  const scrollByCards = (direction = 1) => {
    if (!scrollRef.current) return;
    const amount = scrollRef.current.clientWidth * 0.9;
    scrollRef.current.scrollBy({
      left: amount * direction,
      behavior: "smooth",
    });
  };

  return (
    <section className="py-8 sm:py-10 lg:py-12">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="mb-6 flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
          <div>
            <p className="mb-2 inline-flex rounded-full bg-rose-100 px-3 py-1 text-xs font-semibold uppercase tracking-[0.18em] text-rose-700">
              Compétitions à venir
            </p>
            <h2 className="text-2xl font-black tracking-tight text-slate-900 sm:text-3xl">
              Les 10 prochains compétitions et animations
            </h2>
            <p className="mt-2 max-w-2xl text-sm text-slate-600 sm:text-base">
              Retrouve rapidement les événements à venir de la Ligue et des
              comités.
            </p>
          </div>

          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => scrollByCards(-1)}
              className="inline-flex h-11 w-11 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-700 shadow-sm transition hover:bg-slate-50"
              aria-label="Défiler vers la gauche"
            >
              <FaArrowLeft />
            </button>

            <button
              type="button"
              onClick={() => scrollByCards(1)}
              className="inline-flex h-11 w-11 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-700 shadow-sm transition hover:bg-slate-50"
              aria-label="Défiler vers la droite"
            >
              <FaArrowRight />
            </button>

            <button
              type="button"
              onClick={() => navigate("/calendrier-ligue")}
              className="inline-flex items-center gap-2 rounded-full bg-slate-900 px-4 py-3 text-sm font-bold text-white shadow-sm transition hover:bg-slate-800"
            >
              <FaCalendarAlt />
              Voir le calendrier
            </button>
          </div>
        </div>

        {loading ? (
          <div className="flex snap-x gap-4 overflow-hidden">
            {[...Array(3)].map((_, index) => (
              <div
                key={index}
                className="h-[360px] w-[86%] shrink-0 rounded-[24px] border border-slate-200 bg-white sm:w-[48%] lg:w-[31.6%]"
              >
                <div className="h-48 animate-pulse rounded-t-[24px] bg-slate-200" />
                <div className="space-y-3 p-5">
                  <div className="h-4 w-24 animate-pulse rounded bg-slate-200" />
                  <div className="h-6 w-3/4 animate-pulse rounded bg-slate-200" />
                  <div className="h-4 w-2/3 animate-pulse rounded bg-slate-200" />
                  <div className="h-4 w-1/2 animate-pulse rounded bg-slate-200" />
                </div>
              </div>
            ))}
          </div>
        ) : hasEvents ? (
          <div
            ref={scrollRef}
            className="flex snap-x gap-4 overflow-x-auto pb-2 [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden"
          >
            {events.map((event) => (
              <EventCard key={event.id} event={event} />
            ))}
          </div>
        ) : (
          <div className="rounded-[24px] border border-dashed border-slate-300 bg-white p-8 text-center">
            <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-rose-100 text-rose-600">
              <FaTrophy className="text-xl" />
            </div>
            <h3 className="text-lg font-bold text-slate-900">
              Aucun événement à venir
            </h3>
            <p className="mt-2 text-sm text-slate-600">
              Aucun événement futur avec organisateur renseigné n’est disponible
              pour le moment.
            </p>
            <button
              type="button"
              onClick={() => navigate("/calendrier-ligue")}
              className="mt-5 inline-flex items-center gap-2 rounded-full bg-slate-900 px-4 py-3 text-sm font-bold text-white transition hover:bg-slate-800"
            >
              <FaCalendarAlt />
              Ouvrir le calendrier
            </button>
          </div>
        )}
      </div>
    </section>
  );
}

export default HomeUpcomingEvents;
